import { useState } from 'react';
import { useDebouncedSearch } from './useDebouncedSearch';

/** A Jira issue key, the shape the backend's `issueKeyParam` accepts. */
export const ISSUE_KEY = /^[A-Z][A-Z0-9]+-\d+$/i;

/**
 * The « Parent ticket » field: debounced, committed upper-cased, never re-sent nor sent malformed
 * (the backend would answer 400). `malformed` turns true once the debounce settles on a bad key.
 * A malformed key never reaches the parent's filters, so a desk change clears it here.
 */
export const useParentKeyInput = (
  value: string | undefined,
  serviceDeskId: number,
  onCommit: (key: string | undefined) => void
): [string, (next: string) => void, boolean] => {
  const [rejected, setRejected] = useState('');
  const [input, setTyped] = useDebouncedSearch(value, (key) => {
    if (!key) onCommit(undefined);
    else if (!ISSUE_KEY.test(key)) setRejected(key);
    else if (key.toUpperCase() !== value) onCommit(key.toUpperCase());
  });
  // Cleared with the text, so retyping the same bad key waits for the debounce again.
  const setInput = (next: string) => {
    if (!next.trim()) setRejected('');
    setTyped(next);
  };
  const [desk, setDesk] = useState(serviceDeskId);
  if (desk !== serviceDeskId) {
    setDesk(serviceDeskId);
    setInput('');
  }
  return [input, setInput, !!rejected && rejected === input.trim()];
};
